// ============================================================================
// workbenchShared.js
// Shared constants, normalisation, and persistence for the Consulting Workbench.
// Extracted from ConsultingWorkspace.jsx to allow each sub-tab component
// to import only what it needs without circular dependencies.
// ============================================================================

import {
  getModelID, getProcessModelTitle, getAppVersion,
  getConsultingWorkspace, setConsultingWorkspace,
} from '@process-advisor/process-miner/processMiner/processMiner';

// ── Re-exports for convenience ──────────────────────────────────────────────
export { getModelID, getProcessModelTitle, getAppVersion, getConsultingWorkspace, setConsultingWorkspace };

// ── Forge / ServiceNow bridge ───────────────────────────────────────────────
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};
let _snApi = null;
export function setSNApiForWorkspace(snApi) { _snApi = snApi; }
export function getSNApi() { return _snApi; }

// ── Deliverables ────────────────────────────────────────────────────────────
export const DELIVERABLES = [
  ['quickHealthcheck',    'Quick Healthcheck',        'ai_quick_healthcheck'],
  ['processHealth',       'Process Health Assessment', 'ai_process_health_assessment'],
  ['aiIntelligence',      'AI, Automation & Optimisation',          'ai_intelligence'],
  ['automationPacks',     'AI & Automation Packs',     null],
  ['executionStrategy',   'Execution Strategy',        'ai_execution_strategy'],
  ['frameworkAssessment', 'Framework Assessment',      'ai_framework_assessment'],
  ['advisorReport',       'Assessment Report',         'ai_consulting_report'],
];

export const DELIVERABLE_STATUS = [
  ['not_started', 'Not started'],
  ['draft', 'Draft'],
  ['reviewed', 'Reviewed'],
  ['final', 'Final'],
];

export const DELIVERABLE_DESCRIPTIONS = {
  quickHealthcheck:    'Short AI summary of strengths, risks, and quick wins.',
  processHealth:       'Evidence-based assessment of current process health and issues.',
  aiIntelligence:      'AI-generated opportunities for improvement and optimisation.',
  automationPacks:     'Automation or configuration recommendations ready for delivery.',
  executionStrategy:   'Prioritised execution approach and rollout guidance.',
  frameworkAssessment: 'Assessment against frameworks such as ITIL or COBIT.',
  advisorReport:       'Final client-ready assessment report consolidating curated findings, actions, evidence, value analysis, and next steps.',
};

// Map deliverable keys to the Advisor sub-tab id (for "Go to" navigation)
export const DELIVERABLE_TAB_MAP = {
  quickHealthcheck:    'quickHealthcheck',
  processHealth:       'processHealth',
  aiIntelligence:      'aiIntelligence',
  automationPacks:     'automationPacks',
  executionStrategy:   'aiExecutionStrategy',
  frameworkAssessment: 'frameworkAssessment',
  advisorReport:       'consultingReport',
};

// ── Finding / Action enums ──────────────────────────────────────────────────
export const FINDING_AREAS     = ['Performance', 'Compliance', 'Automation', 'Cost', 'Experience', 'Data Quality', 'Governance'];
export const SEVERITIES        = ['Low', 'Medium', 'High', 'Critical'];
export const CONFIDENCE        = ['Weak', 'Moderate', 'Strong'];
export const VALUES            = ['Low', 'Medium', 'High'];
export const EFFORTS           = ['Low', 'Medium', 'High'];
export const FINDING_STATUSES  = ['Needs Review', 'Validated', 'Converted to Action', 'Parked', 'Rejected'];
export const ACTION_STATUSES   = ['Proposed', 'Accepted', 'In Progress', 'Blocked', 'Implemented', 'Rejected'];
export const ACTION_TYPES      = ['Process Change', 'Automation', 'Configuration', 'Governance', 'Data Quality', 'Training', 'Further Analysis'];

// ── Helpers ─────────────────────────────────────────────────────────────────
export const splitLines = (value) =>
  Array.isArray(value) ? value : String(value || '').split('\n').map(v => v.trim()).filter(Boolean);

const mapFindingStatus = (status) => {
  if (status === 'New' || !status) return 'Needs Review';
  if (status === 'Accepted' || status === 'Implemented') return 'Validated';
  return FINDING_STATUSES.includes(status) ? status : 'Needs Review';
};

export const normaliseFinding = (finding = {}) => ({
  id:              finding.id || `finding_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  findingID:       finding.findingID || finding.finding_id || '',
  title:           finding.title || finding.finding || 'Untitled finding',
  finding:         finding.finding || finding.title || '',
  area:            FINDING_AREAS.includes(finding.area || finding.category) ? (finding.area || finding.category) : 'Performance',
  severity:        SEVERITIES.includes(finding.severity) ? finding.severity : 'Medium',
  confidence:      CONFIDENCE.includes(finding.confidence) ? finding.confidence : 'Moderate',
  valuePotential:  VALUES.includes(finding.valuePotential) ? finding.valuePotential : 'Medium',
  effort:          EFFORTS.includes(finding.effort) ? finding.effort : 'Medium',
  status:          mapFindingStatus(finding.status),
  sourceArea:      finding.sourceArea || finding.source || '',
  objectType:      finding.objectType || '',
  objectLabel:     finding.objectLabel || '',
  objectId:        finding.objectId || '',
  evidence:        splitLines(finding.evidence),
  recommendation:  finding.recommendation || '',
  action:          finding.action || '',
  owner:           finding.owner || '',
  targetDate:      finding.targetDate || '',
  consultantNotes: finding.consultantNotes || finding.notes || '',
  filters:         Array.isArray(finding.filters) ? finding.filters : [],
  filterSummary:   Array.isArray(finding.filterSummary) ? finding.filterSummary : [],
  createdAt:       finding.createdAt || new Date().toISOString(),
  updatedAt:       finding.updatedAt || new Date().toISOString(),
});

export const normaliseAction = (action = {}) => ({
  id:                  action.id || `action_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  findingId:           action.findingId || '',
  title:               action.title || 'Untitled action',
  actionType:          ACTION_TYPES.includes(action.actionType) ? action.actionType : 'Process Change',
  status:              ACTION_STATUSES.includes(action.status) ? action.status : 'Proposed',
  priority:            SEVERITIES.includes(action.priority) ? action.priority : 'Medium',
  expectedValue:       VALUES.includes(action.expectedValue) ? action.expectedValue : 'Medium',
  effort:              EFFORTS.includes(action.effort) ? action.effort : 'Medium',
  owner:               action.owner || '',
  targetDate:          action.targetDate || '',
  evidenceSummary:     action.evidenceSummary || '',
  implementationSteps: action.implementationSteps || '',
  successMeasure:      action.successMeasure || '',
  notes:               action.notes || '',
  createdAt:           action.createdAt || new Date().toISOString(),
  updatedAt:           action.updatedAt || new Date().toISOString(),
});

const emptyDeliverables = () => DELIVERABLES.reduce((acc, [key]) => ({ ...acc, [key]: 'not_started' }), {});

export const createDefaultWorkspace = () => ({
  version: 6,
  objective: '', scope: '', businessArea: '', processOwner: '', stakeholders: '',
  targetSla: '', painPoints: '', workingHypothesis: '', notes: '',
  findings: [], actions: [], discovery_rules: [], deliverables: emptyDeliverables(), updatedAt: '',
});

export const normaliseWorkspace = (value) => {
  let parsed = value;
  if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch (_) { parsed = null; } }
  const base = createDefaultWorkspace();
  if (!parsed || typeof parsed !== 'object') return base;
  return {
    ...base,
    ...parsed,
    version: 6,
    findings:       Array.isArray(parsed.findings) ? parsed.findings.map(normaliseFinding) : [],
    actions:        Array.isArray(parsed.actions) ? parsed.actions.map(normaliseAction) : [],
    discovery_rules: Array.isArray(parsed.discovery_rules) ? parsed.discovery_rules : [],
    deliverables:   { ...base.deliverables, ...(parsed.deliverables || {}) },
  };
};

// ── Persistence ─────────────────────────────────────────────────────────────
const storageKey = (modelId) => `metricus.consultingWorkspace.${modelId || 'unsaved'}`;

export const loadLocalWorkspace = (modelId) => {
  try {
    const raw = window.localStorage.getItem(storageKey(modelId));
    return raw ? normaliseWorkspace(raw) : null;
  } catch (_) { return null; }
};

export const saveLocalWorkspace = (modelId, workspace) => {
  try { window.localStorage.setItem(storageKey(modelId), JSON.stringify(workspace)); } catch (_) {}
};

const workspaceHasContent = (workspace) => {
  let parsed = workspace;
  if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch (_) { parsed = null; } }
  if (!parsed || typeof parsed !== 'object') return false;
  return Boolean(
    (Array.isArray(parsed.findings) && parsed.findings.length) ||
    (Array.isArray(parsed.actions) && parsed.actions.length) ||
    parsed.objective || parsed.scope || parsed.notes || parsed.updatedAt
  );
};

const workspaceTimestamp = (workspace) => {
  const value = workspace?.updatedAt || workspace?.lastUpdated || workspace?.createdAt || '';
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
};

const mergeWorkspaceRecords = (primary = [], secondary = []) => {
  const map = new Map();
  [...secondary, ...primary].forEach((item) => {
    if (!item) return;
    const id = item.id || item.findingID || item.finding_id || item.title || item.finding || `${Date.now()}_${Math.random()}`;
    map.set(id, item);
  });
  return Array.from(map.values());
};

const chooseWorkspace = (backend, local, memory) => {
  const candidates = [backend, local, memory].filter(workspaceHasContent);
  if (!candidates.length) return createDefaultWorkspace();
  const selected = candidates.sort((a, b) => workspaceTimestamp(b) - workspaceTimestamp(a))[0];
  const merged = candidates.reduce((acc, ws) => ({
    ...acc,
    ...ws,
    findings:        mergeWorkspaceRecords(ws.findings, acc.findings),
    actions:         mergeWorkspaceRecords(ws.actions, acc.actions),
    discovery_rules: mergeWorkspaceRecords(ws.discovery_rules, acc.discovery_rules),
    deliverables:    { ...(acc.deliverables || {}), ...(ws.deliverables || {}) },
  }), selected);
  return normaliseWorkspace(merged);
};

export const saveWorkspaceBestEffort = async ({ modelId, workspace, AppVersion }) => {
  if (!modelId) return { savedToBackend: false };
  saveLocalWorkspace(modelId, workspace);
  setConsultingWorkspace(workspace);
  try {
    if (AppVersion === 'FORGE') {
      const { invoke } = await loadForgeModule();
      await invoke('saveConsultingWorkspace', { modelId, consulting_workspace: JSON.stringify(workspace) });
      return { savedToBackend: true };
    }
    if (AppVersion === 'SNOW' && _snApi && typeof _snApi.saveConsultingWorkspace === 'function') {
      await _snApi.saveConsultingWorkspace({ modelId, consulting_workspace: JSON.stringify(workspace) });
      return { savedToBackend: true };
    }
  } catch (error) {
    console.warn('[ConsultingWorkspace] Backend save failed; local copy retained.', error);
  }
  return { savedToBackend: false };
};

export const loadWorkspaceBestEffort = async ({ modelId, AppVersion }) => {
  const local = loadLocalWorkspace(modelId);
  const memory = normaliseWorkspace(getConsultingWorkspace());
  let backend = null;
  try {
    if (modelId && AppVersion === 'FORGE') {
      const { invoke } = await loadForgeModule();
      const result = await invoke('getConsultingWorkspace', { modelId });
      const raw = result?.consulting_workspace || result;
      backend = workspaceHasContent(raw) ? normaliseWorkspace(raw) : null;
    } else if (modelId && AppVersion === 'SNOW' && _snApi && typeof _snApi.getConsultingWorkspace === 'function') {
      const result = await _snApi.getConsultingWorkspace({ modelId });
      const raw = result?.consulting_workspace || result;
      backend = workspaceHasContent(raw) ? normaliseWorkspace(raw) : null;
    }
  } catch (_) {}
  const selected = chooseWorkspace(backend, local, memory);
  setConsultingWorkspace(selected);
  if (modelId) saveLocalWorkspace(modelId, selected);
  return selected;
};

// ── Scoring / display helpers ───────────────────────────────────────────────
export const scoreMap = { Low: 1, Medium: 2, High: 3, Critical: 4 };
export const actionScore = (action) => (scoreMap[action.priority] || 2) + (scoreMap[action.expectedValue] || 2) - (scoreMap[action.effort] || 2);

export const statusTone = (status) => {
  switch (status) {
    case 'Validated': case 'Accepted':                     return 'green';
    case 'Converted to Action': case 'Implemented': case 'In Progress': return 'blue';
    case 'Parked':                                         return 'purple';
    case 'Rejected': case 'Blocked':                       return 'red';
    default:                                               return 'neutral';
  }
};

export const focusLabel = (item) =>
  [item.sourceArea, item.objectType, item.objectLabel].filter(Boolean).join(' \u00b7 ');
